import { useTheme, type DefaultTheme } from 'styled-components';
import { EventStatusEnum } from '../../types/types';
import { Icon, IconName, type IconProps } from '../Icons';
import { IndicatorDotWrapper } from '../IndicatorDot';
import { Wrapper } from './styles';
import { OrderStatusSize } from './types';

type EventStatusDisplay = {
  icon: IconName;
  color: string;
};

export const getPredictionMarketEventStatusDisplay = (
  status: EventStatusEnum | undefined,
  theme: DefaultTheme
): EventStatusDisplay => {
  switch (status) {
    case EventStatusEnum.Open:
      return {
        icon: IconName.Circle,
        color: theme.colors.blue.lighten,
      };
    case EventStatusEnum.Closed:
      return {
        icon: IconName.Clock,
        color: theme.colors.yellow.default,
      };
    case EventStatusEnum.Resolved:
      return {
        icon: IconName.CheckCircleSolid,
        color: theme.colors.green.default,
      };
    default:
      return {
        icon: IconName.Circle,
        color: theme.colors.gray.main,
      };
  }
};

const EventStatusIcon = ({
  status,
  ...props
}: { status: EventStatusEnum; theme?: DefaultTheme } & Omit<IconProps, 'color' | 'icon'>) => {
  const defaultTheme = useTheme();
  const theme = props.theme ?? defaultTheme;
  const config = getPredictionMarketEventStatusDisplay(status, theme);

  return <Icon icon={config.icon} color={config.color} {...props} />;
};

type PredictionMarketEventStatusProps = {
  status: EventStatusEnum;
  align?: 'right' | 'left';
  iconPlacement?: 'right' | 'left';
  theme?: DefaultTheme;
  size?: OrderStatusSize;
};

export function PredictionMarketEventStatus({
  status,
  align = 'left',
  iconPlacement = 'left',
  size = OrderStatusSize.Default,
  ...props
}: PredictionMarketEventStatusProps) {
  const defaultTheme = useTheme();
  const theme = props.theme ?? defaultTheme;
  const iconSize = size === OrderStatusSize.Default ? 'fontSizeLarge' : 'fontSizeSmall';

  return (
    <Wrapper
      iconPlacement={iconPlacement}
      align={align}
      theme={theme}
      size={size}
      data-testid="prediction-market-event-status"
    >
      {status}
      <IndicatorDotWrapper show={false} theme={theme}>
        <EventStatusIcon status={status} theme={theme} size={iconSize} />
      </IndicatorDotWrapper>
    </Wrapper>
  );
}
